/**
 * Cmarkdownxychart Component
 * Renders a Mermaid-style XY chart inside a Cmarkdownvisual frame.
 *
 * The chart model owns:
 *   - parsed data
 *   - container size (ResizeObserver)
 *   - zoom level (controlled via toolbar +/-)
 *   - hover state (series + category)
 *
 * The visuals are split into XYChartCanvas (SVG) and XYChartCompactPreview
 * (widget tile). Toolbar buttons (CSV / PNG / zoom) are rendered into the
 * Cmarkdownvisual `controls` and `modalControls` slots.
 */

"use client";

import { type CSSProperties, type ReactElement } from "react";
import {
  Cmarkdownvisual,
  CmarkdownvisualError,
  CmarkdownvisualErrorReason,
  CmarkdownvisualType,
} from "../Cmarkdownvisual";

import { XYChartFrame } from "./XYChartFrame";
import { useCmarkdownXYChartModel } from "./useCmarkdownXYChartModel";
import { XYCHART_DEFAULT_CANVAS_HEIGHT } from "./xychart-constants";

const VISUAL_CONTENT_STYLE: CSSProperties = {
  minHeight: XYCHART_DEFAULT_CANVAS_HEIGHT,
  flex: 1,
  padding: 0,
  display: "flex",
  flexDirection: "column",
};

export interface CmarkdownxychartProps {
  content: string;
  className?: string;
  style?: CSSProperties;
}

export function Cmarkdownxychart({
  content,
  className,
  style,
}: CmarkdownxychartProps): ReactElement {
  const chart = useCmarkdownXYChartModel({ content });

  if (chart.parsed == null || chart.chrome == null) {
    return (
      <CmarkdownvisualError
        dataElScope="Cmarkdownxychart/Visual"
        type={CmarkdownvisualType.XyChart}
        reason={CmarkdownvisualErrorReason.Unparseable}
      />
    );
  }

  return (
    <Cmarkdownvisual
      dataElScope="Cmarkdownxychart/Visual"
      type={chart.chrome.chartKindLabel}
      title={chart.parsed.title}
      contentStyle={VISUAL_CONTENT_STYLE}
      className={className}
      style={style}
      compactPreview={chart.compactPreview}
      metric={chart.chrome.metric}
      controls={chart.toolbarControls}
      modalControls={chart.modalControls}
    >
      <XYChartFrame
        parsed={chart.parsed}
        layout={chart.layout}
        uniqueSvgId={chart.uniqueSvgId}
        viewportRef={chart.viewportRef}
        svgRef={chart.svgRef}
        hoveredSeriesIndex={chart.hoveredSeriesIndex}
        hoveredCategoryIndex={chart.hoveredCategoryIndex}
        hoverEntries={chart.hoverEntries}
        popoverModel={chart.popoverModel}
        onHoverSeries={chart.onHoverSeries}
        onHoverCategory={chart.onHoverCategory}
      />
    </Cmarkdownvisual>
  );
}
